import { Box, FormHelperText, List, ListItem, Stack, Theme } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProps } from '../helpers';
import { createUseStyles } from 'react-jss';
import Text from '@/components/text';
import { UiOption } from '@/lib/helpers';
import Switch from '../switch';
import Tooltip from '@/components/tooltip';
import TextInput from '../text-input';
import { cloneDeep, debounce } from 'lodash';
import FlatButton from '@/components/flat-button';
import Chip from '@/components/chip';
import SelectMulti from '../select-multi';

interface Props extends FormProps {
  options: UiOption[];
  value: string[];
  onChange: (values: string[]) => void;
  height?: string | number;
  search?: boolean;
  categoryOptions?: UiOption[];
  onSettings?: (option: UiOption) => void;
}

const useStyles = createUseStyles<string, { height: string | number }>((theme: Theme) => ({
  switchList: ({ height }) => ({
    border: `1px solid ${theme.palette.gray}`,
    borderRadius: theme.shape.borderRadius,
    '& ul': {
      height: height || 'auto',
      overflowY: 'auto',
      '&.MuiList-root': {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    '& .MuiFormControl-root': {
      padding: 0,
      margin: 0,
    },
    '& li.MuiListItem-root': {
      paddingTop: 0,
      paddingBottom: 0,
      '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
  }),
  header: {
    padding: 10,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: 10,
  },
  selectAllButton: {
    marginRight: 7,
  },
}));

const getOptionCategories = (meta?: Record<string, unknown>): string[] => {
  if (!Array.isArray(meta?.categories)) {
    return [];
  }
  return meta.categories as string[];
};

const SwitchList: FC<Props> = ({
  error,
  onChange,
  options,
  name,
  value,
  disabled,
  readonly,
  height = '',
  emptyMessage = '',
  search = false,
  categoryOptions = [],
  onSettings,
}) => {
  const styles = useStyles({ height });
  const [keyword, setKeyword] = useState('');
  const [filter, setFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<UiOption[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllEnabled, setSelectAllEnabled] = useState(true);

  const filteredOptions = useMemo(() => {
    if (!filter && !categoryFilter?.length) {
      return options;
    }

    let filteredOptions = cloneDeep(options);

    // Filter by categories
    const selectedCategories = new Set(categoryFilter.map(({ value }) => value));
    if (categoryFilter?.length) {
      filteredOptions = filteredOptions.filter(({ meta }) => {
        const optionCategories = getOptionCategories(meta);
        return optionCategories.some((category) => selectedCategories.has(category));
      });
    }

    // Filter by keyword
    if (filter) {
      const safeFilter = String(filter).toLowerCase().trim();
      filteredOptions = filteredOptions.filter(({ label, meta }) => {
        const labelMatch = String(label).toLowerCase().includes(safeFilter);

        if (labelMatch) {
          return true;
        }

        const optionCategories = getOptionCategories(meta);
        const categoryLabelMatch = optionCategories.some((category) => {
          const safeCategory = String(category).toLowerCase();
          return safeCategory.includes(safeFilter);
        });

        return categoryLabelMatch;
      });
    }

    return filteredOptions;
  }, [options, filter, categoryFilter]);

  const handleSettings = (option: UiOption) => {
    onSettings?.(option);
  };

  const handleChange = (value: string[]) => {
    onChange(value);
    if (value?.length === filteredOptions.length) {
      setSelectAll(true);
    }
  };

  const handleSwitchChange = (optValue: string, checked: boolean) => {
    let updatedValue = value ? [...value] : [];
    if (checked) {
      updatedValue.push(optValue);
    } else {
      updatedValue = updatedValue.filter((val) => val !== optValue);
    }
    handleChange(updatedValue);
  };

  const handleSelectAll = () => {
    const updated = !selectAll;
    setSelectAll(updated);

    let updatedValue = value ? [...value] : [];

    filteredOptions.forEach(({ disabled, value: optValue }) => {
      if (disabled) {
        return;
      }

      if (updated) {
        updatedValue.push(optValue);
      } else {
        updatedValue = updatedValue.filter((val) => val !== optValue);
      }
    });

    handleChange(updatedValue);
  };

  const handleChangeKeyword = (value: string) => {
    setKeyword(value);
    debounceHandleChangeFilter(value);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceHandleChangeFilter = useCallback(
    debounce((keyword: string) => {
      setFilter(String(keyword).trim().toLowerCase());
    }, 400),
    []
  );

  const handleCategorySelectChange = (selectedCategories: UiOption[]) => {
    setCategoryFilter(selectedCategories);
  };

  useEffect(() => {
    if (!filteredOptions.length) {
      setSelectAllEnabled(false);
      return;
    }
    setSelectAllEnabled(true);
    setSelectAll(
      filteredOptions.every(
        ({ value: optValue, disabled }) => value?.includes(optValue) || disabled
      )
    );
  }, [filteredOptions, value]);

  const hasError = !!error;
  const hasItems = !!filteredOptions?.length;
  const hasFilter = !!filter;
  const hasCategories = !!categoryOptions.length;

  return (
    <Stack className={styles.switchList}>
      <Stack
        direction="row"
        className={styles.header}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction="row" gap={1}>
          <Box width="200px">
            {search && (
              <TextInput
                name="keyword"
                onChange={handleChangeKeyword}
                value={keyword}
                size="small"
                placeholder="Filter by name"
                clearable
                fullWidth
              />
            )}
          </Box>
          {hasCategories && (
            <Box width="300px">
              <SelectMulti
                name="select-multi-filter"
                options={categoryOptions || []}
                value={categoryFilter.map(({ value }) => value)}
                onChange={handleCategorySelectChange}
                size="small"
                label="Filter by category"
                light
              />
            </Box>
          )}
        </Stack>
        <Box mr={1}>
          <FlatButton
            label={selectAll ? 'Disable All' : 'Enable All'}
            onClick={handleSelectAll}
            disabled={disabled || readonly || !selectAllEnabled}
            size="small"
            color="link"
          />
        </Box>
      </Stack>

      <List>
        {!hasItems && (
          <ListItem>
            <Box mt={2}>
              <Text italic color="grey">
                {hasFilter ? 'No matches found' : emptyMessage || 'No options available'}
              </Text>
            </Box>
          </ListItem>
        )}
        {filteredOptions.map((option, index) => {
          const { label, value: optValue, disabled: optDisabled, tooltip, meta } = option;
          const checked = !!value?.includes(optValue);

          const { hasSettings = false, categories = [] } =
            (meta as { hasSettings: boolean; categories: string[] }) || {};
          const hasCategories = !!categories?.length;

          let SwitchEl = (
            <Switch
              name={name}
              checked={checked}
              onChange={(checked) => handleSwitchChange(optValue, checked)}
              disabled={optDisabled || disabled || readonly}
              fullWidth={false}
            />
          );

          if (tooltip) {
            SwitchEl = <Tooltip title={tooltip}>{SwitchEl}</Tooltip>;
          }

          return (
            <ListItem key={index}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                py={2}
              >
                <Stack direction="column" gap={1} width="100%">
                  <Box flexGrow={1}>{label}</Box>
                  {hasCategories && (
                    <Stack direction="row" gap={1}>
                      {categories.map((category) => (
                        <Chip key={category} label={category} size="x-small" />
                      ))}
                    </Stack>
                  )}
                </Stack>
                <Stack gap={2} direction="row" alignItems="center">
                  {hasSettings && (
                    <FlatButton
                      label="Settings"
                      icon="settings"
                      size="small"
                      onClick={() => handleSettings(option)}
                      disabled={!checked}
                    />
                  )}
                  <Box minWidth="42px">{SwitchEl}</Box>
                </Stack>
              </Stack>
            </ListItem>
          );
        })}
      </List>
      {hasError && <FormHelperText error>{error}</FormHelperText>}
    </Stack>
  );
};

export default SwitchList;
